import { SyncEngine, type SyncEngineOptions } from './engine';
import { SyncStore } from './store';
import type { StoreChange, SyncCoordinator, SyncStorage } from './types';

export type SyncClient = {
  store: SyncStore;
  engine: SyncEngine;
  /** Stops syncing, waits for pending writes to reach storage and releases it. Safe to repeat. */
  close(): Promise<void>;
};

/**
 * Loads persisted data, then keeps storage and other tabs up to date with every change.
 * Storage writes run one at a time, in order.
 */
export async function createSyncClient(
  options: Omit<SyncEngineOptions, 'store'> & {
    storage: SyncStorage;
    coordinator?: SyncCoordinator;
    store?: SyncStore;
  },
): Promise<SyncClient> {
  const { storage, coordinator } = options;
  const store = options.store ?? new SyncStore();
  store.hydrate(await storage.load());

  let writes = Promise.resolve();
  const persist = (change: StoreChange) => {
    writes = writes
      .then(() => storage.write(change))
      .catch((error: unknown) => console.error('Local storage write failed', error));
  };

  const stopPersisting = store.onChange((change) => {
    persist(change);
    coordinator?.publish(change);
  });
  const stopListening = coordinator?.subscribe((change) => store.applyExternal(change));

  const engine = new SyncEngine({ ...options, store, coordinator });

  let closing: Promise<void> | null = null;
  return {
    store,
    engine,
    close() {
      closing ??= (async () => {
        engine.stop();
        stopPersisting();
        stopListening?.();
        coordinator?.close();
        await writes;
        storage.close?.();
      })();
      return closing;
    },
  };
}

/** Storage that keeps nothing: for tests and as a fallback when the browser offers none. */
export function memoryStorage(): SyncStorage & { snapshot(): StoreChange[] } {
  let cursor = 0;
  const entries = new Map<string, StoreChange['put'][number]>();
  const log: StoreChange[] = [];
  return {
    async load() {
      return { cursor, entries: [...entries.values()] };
    },
    async write(change) {
      log.push(change);
      for (const key of change.remove) entries.delete(`${key.collection}:${key.id}`);
      for (const entry of change.put) entries.set(`${entry.collection}:${entry.record.id}`, entry);
      if (change.cursor !== undefined) cursor = change.cursor;
    },
    async clear() {
      entries.clear();
      cursor = 0;
    },
    snapshot: () => log,
  };
}
